import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { homeForRole } from "@/components/ProtectedRoute";
import InstallerShell from "@/components/installer/InstallerShell";
import CustomerShell from "@/components/CustomerShell";
import { PASSWORD_INPUT_PROPS } from "@/utils/passwordInputProps";
import { toast } from "sonner";
import BackButton from "@/components/BackButton";

/**
 * `/settings/change-password` — signed-in users change their own password.
 * Server re-validates against the current password policy + history. Kept
 * intentionally minimal — a single form, no side channels, no navigation
 * away until success.
 */
export default function ChangePassword() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const Shell = user?.role === "installer" ? InstallerShell : CustomerShell;

    async function onSubmit(e) {
        e.preventDefault();
        if (next !== confirm) { setError("Passwords don't match."); return; }
        setBusy(true); setError("");
        try {
            await api.changePassword(current, next, confirm);
            toast.success("Password updated");
            // DEV-013A · when the user landed here because
            // `force_password_change=true`, refresh the auth context so
            // the flag flips back to false and the app unlocks. Then
            // route to the natural home for the role.
            await refresh();
            navigate(homeForRole(user?.role));
        } catch (err) {
            setError(formatApiError(err?.response?.data?.detail) || "Failed to change password");
        } finally { setBusy(false); }
    }

    return (
        <Shell>
            <div className="mx-auto max-w-lg px-4 md:px-8 pt-8 pb-16" data-testid="change-password-page">
                {user?.force_password_change ? (
                    <div
                        className="mb-4 rounded-md border border-[#F59E0B]/40 bg-[#FEF3C7] px-3.5 py-2.5 text-xs text-[#7C4A03]"
                        data-testid="forced-password-change-banner"
                    >
                        A Security Administrator has issued you a temporary password.
                        Set a new password to regain access to Pearl - EnergyFlow.
                    </div>
                ) : (
                    <BackButton
                        fallbackHref={user?.role === "installer" ? "/installer" : user?.role === "operations" ? "/operations" : "/dashboard"}
                        testid="change-password-back"
                        className="mb-4"
                    />
                )}
                <h1 className="text-2xl md:text-3xl text-[#131B2F] font-medium">Change password</h1>
                <p className="mt-1 text-sm text-[#4B5565]">
                    You'll need to sign in again on other devices after changing your password.
                </p>

                <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="change-password-form">
                    <Field label="Current password">
                        <input
                            data-testid="cp-current" type="password" required autoComplete="current-password"
                            {...PASSWORD_INPUT_PROPS}
                            value={current} onChange={(e) => setCurrent(e.target.value)}
                            className="w-full h-11 rounded-md border border-[#E5E7EB] bg-white px-3.5 text-base outline-none focus:border-[#3E9E2E]"
                        />
                    </Field>

                    <Field label="New password">
                        <input
                            data-testid="cp-new" type="password" required minLength={12} autoComplete="new-password"
                            {...PASSWORD_INPUT_PROPS}
                            value={next} onChange={(e) => setNext(e.target.value)}
                            className="w-full h-11 rounded-md border border-[#E5E7EB] bg-white px-3.5 text-base outline-none focus:border-[#3E9E2E]"
                        />
                        <p className="mt-1 text-xs text-[#4B5565]">
                            At least 12 characters. Must not match your name, email or any of your last 5 passwords.
                        </p>
                    </Field>

                    <Field label="Confirm new password">
                        <input
                            data-testid="cp-confirm" type="password" required minLength={12} autoComplete="new-password"
                            {...PASSWORD_INPUT_PROPS}
                            value={confirm} onChange={(e) => setConfirm(e.target.value)}
                            className="w-full h-11 rounded-md border border-[#E5E7EB] bg-white px-3.5 text-base outline-none focus:border-[#3E9E2E]"
                        />
                    </Field>

                    {error && <div className="text-sm text-[#DA3535]" data-testid="cp-error">{error}</div>}

                    <button type="submit" disabled={busy}
                        data-testid="cp-submit"
                        className="w-full h-11 rounded-md bg-[#3E9E2E] hover:bg-[#2E7E22] disabled:opacity-70 text-white font-medium">
                        {busy ? "Updating…" : "Update password"}
                    </button>
                </form>
            </div>
        </Shell>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="block text-sm text-[#4B5565] mb-1.5">{label}</label>
            {children}
        </div>
    );
}
